import { useMemo } from 'react';

import { Location, useLocation } from 'react-router';

import toPairs from 'lodash/toPairs';

import Constants from '@/constants';

type RouteMatches =
  (typeof Constants.ROUTE_PATH_GROUPS)[keyof typeof Constants.ROUTE_PATH_GROUPS];

export const encodeQueryString = (
  pairs: Record<string, string | undefined>,
): string =>
  toPairs(pairs)
    .map(([key, value]) => {
      const encodedKey = encodeURIComponent(key);
      const encodedValue = encodeURIComponent(value ?? '');
      return `${encodedKey}=${encodedValue}`;
    })
    .join('&');

/** Allow only same-origin relative paths for query-param navigation targets. */
export const sanitizeInternalPath = (path: string): string | null => {
  const trimmed = path.trim();
  if (!trimmed.startsWith('/') || trimmed.startsWith('//')) {
    return null;
  }
  const lower = trimmed.toLowerCase();
  if (lower.startsWith('/http') || lower.includes('://')) {
    return null;
  }
  return trimmed;
};

export const getKeyValuePairsFromHash = (location: {
  hash: string;
}): Record<string, string> => {
  const hash = location.hash || '';
  if (hash.length) {
    const hashString = hash.substr(1);
    const replaceChars = decodeURI(hashString)
      .replace(/"/g, '\\"')
      .replace(/&/g, '","')
      .replace(/=/g, '":"');
    try {
      const parsed: unknown = JSON.parse(`{"${replaceChars}"}`);

      if (typeof parsed !== 'object' || parsed === null) {
        return {};
      }

      return parsed as Record<string, string>;
    } catch (e) {
      console.error('Error in getKeyValuePairsFromHash:', e);
      return {};
    }
  }
  return {};
};

const getMatches = (location: Location) => {
  const currPath = location.pathname.split('/').filter(Boolean);
  const p = Constants.ROUTE_PATH_NAMES;
  const routeMatches = Constants.ROUTE_PATH_GROUPS;

  const matches: RouteMatches[] = [];

  if (currPath[0] === 'ai-experiments') {
    matches.push(routeMatches.AiExperimentsPath);
    return matches;
  }

  if (
    currPath.length === 5 &&
    currPath[0] === 'plans' &&
    currPath[2] === 'team-members'
  ) {
    matches.push(routeMatches.TrainingTeamMemberProgressPath);
  }

  if (currPath[1] === p.TEAM_MEMBERS_PATH_NAME && currPath.length >= 4) {
    matches.push(routeMatches.TeamMemberPlan);
  }

  if (
    currPath.includes(p.DOCUMENT_PATH_NAME) &&
    currPath.includes(p.COMPLIANCE_PATH_NAME)
  ) {
    matches.push(routeMatches.ComplianceDocumentPath);
  }

  if (currPath[0] === 'training') {
    matches.push(routeMatches.TrainingPath);

    if (currPath.length === 1) matches.push(routeMatches.TrainingMyPlansPath);

    if (currPath[1] === p.BUILD_PATH_NAME)
      matches.push(routeMatches.TrainingBuildPath);
    if (currPath[1] === p.TRAIN_TOGETHER_MODE_PATH_NAME)
      matches.push(routeMatches.TrainTogetherModePath);
    if (currPath[1] === p.TEAM_PATH_NAME)
      matches.push(routeMatches.TrainingTeamPath);

    if (currPath[1] === p.QUIZZES_PATH_NAME) {
      matches.push(routeMatches.TrainingQuizzesPath);
      if (currPath[2] === p.BUILD_PATH_NAME)
        matches.push(routeMatches.TrainingBuildQuizzesPath);
    }

    if (currPath[1] === p.QUIZ_PATH_NAME) {
      matches.push(routeMatches.TrainingEditQuizzesPath);
    }

    if (currPath[1] === p.TEAM_MEMBERS_PATH_NAME) {
      matches.push(routeMatches.TrainingTeamMembersPath);
      if (currPath.length === 3)
        matches.push(routeMatches.TrainingTeamMembersPath);
      if (currPath.length > 3)
        matches.push(routeMatches.TrainingTeamMemberProgressPath);
    }

    if (currPath[1] === p.ASSIGNED_PATH_NAME) {
      matches.push(routeMatches.TrainingAssignedPath);
      if (currPath[2] === p.TO_COMPLETE_PATH_NAME)
        matches.push(routeMatches.StepDocumentPath);
    }

    if (currPath[1] === p.PLAN_PATH_NAME)
      matches.push(routeMatches.TrainingPlanEditPath);
  }

  matchPlansPath(currPath, matches);

  matchReportsPath(currPath, matches);

  if (currPath[0] === p.ADMIN_PATH_NAME) {
    matches.push(
      currPath.length === 1
        ? routeMatches.AdminRootPath
        : routeMatches.AdminPath,
    );
  }

  // Other broad categories
  if (!currPath.length) matches.push(routeMatches.RootPath);
  if (currPath[0] === p.CATEGORY_PATH_NAME)
    matches.push(routeMatches.CategoriesPath);
  if (currPath[0] === p.COMPLIANCE_PATH_NAME)
    matches.push(routeMatches.CompliancePath);
  if (currPath[0] === p.HEADLESS_PATH_NAME)
    matches.push(routeMatches.SignalPath);
  if (currPath[0] === p.SEARCH_PATH_NAME) matches.push(routeMatches.SearchPath);
  if (currPath[0] === p.SETTINGS_PATH_NAME)
    matches.push(routeMatches.SettingsPath);
  if (currPath[0] === p.DOCUMENT_PATH_NAME)
    matches.push(routeMatches.DetailsPath);
  if (currPath.includes(p.DOCUMENT_PATH_NAME))
    matches.push(routeMatches.DocumentPath);

  if (
    currPath[0] === p.CATEGORY_PATH_NAME ||
    (currPath[0] === p.DOCUMENT_PATH_NAME &&
      !(location as { state?: { isCompliance?: boolean } }).state?.isCompliance)
  ) {
    matches.push(routeMatches.ExplorePath);
  }

  return matches;
};

export const useRouteMatcher = (): RouteMatches[] => {
  const location = useLocation();
  return useMemo(() => getMatches(location), [location]);
};

/** True when the current URL is under `/reports`. */
export const useIsReportsRoute = (): boolean => {
  const routeMatches = useRouteMatcher();
  return routeMatches.includes(Constants.ROUTE_PATH_GROUPS.ReportsPath);
};

const matchPlansPath = (currPath: string[], matches: RouteMatches[]) => {
  const routeMatches = Constants.ROUTE_PATH_GROUPS;

  if (currPath.length === 0 || currPath.at(0) !== 'plans') {
    return;
  }

  matches.push(routeMatches.TrainingPath); // matches /plans

  if (currPath.at(1) === 'my-plans') {
    matches.push(routeMatches.TrainingMyPlansPath); // matches /plans/my-plans

    if (currPath.length <= 2) {
      return;
    }

    if (currPath.length === 3) {
      matches.push(routeMatches.TrainingAssignedPath); // matches /plans/my-plans/:planId
      return;
    }

    if (currPath.length >= 4 && currPath.at(3) === 'doc') {
      matches.push(routeMatches.StepDocumentPath); // /plans/my-plans/:planId/doc/:stepId
      return;
    }
  }

  if (currPath.at(1) === 'manage-plans') {
    matches.push(routeMatches.TrainingTeamPath); // matches /plans/manage-plans

    if (currPath.length === 2) {
      return;
    }

    if (currPath.length === 3) {
      matches.push(routeMatches.TrainingTeamMembersPath); // matches /plans/manage-plans/:planId
      return;
    }

    if (currPath.length === 5 && currPath.at(3) === 'team-member') {
      matches.push(routeMatches.TrainingTeamMemberProgressPath); // matches /plans/manage-plans/:planId/team-member/:teamMemberId
      return;
    }

    if (currPath.length === 4 && currPath.at(3) === 'train-together') {
      matches.push(routeMatches.TrainTogetherModePath); // matches /plans/manage-plans/:planId/train-together
      return;
    }

    if (currPath.length === 4 && currPath.at(3) === 'edit') {
      matches.push(routeMatches.TrainingPlanEditPath); // matches /plans/manage-plans/:planId/edit
      return;
    }

    return;
  }

  if (currPath.at(1) === 'manage-quizzes') {
    if (currPath.length === 2) {
      matches.push(routeMatches.TrainingQuizzesPath); // matches /plans/manage-quizzes
      return;
    }

    if (currPath.length === 3) {
      matches.push(routeMatches.TrainingEditQuizzesPath); // matches /plans/manage-quizzes/:quizId
      return;
    }

    return;
  }
};

const matchReportsPath = (currPath: string[], matches: RouteMatches[]) => {
  const routeMatches = Constants.ROUTE_PATH_GROUPS;

  if (currPath.length === 0 || currPath.at(0) !== 'reports') {
    return;
  }
  if (currPath[0] === 'reports') {
    matches.push(routeMatches.ReportsPath); // matches /reports

    if (currPath[1] === 'plans') matches.push(routeMatches.ReportsPlansPath); // matches /reports/plans
    if (currPath[1] === 'plans' && currPath.length === 3)
      matches.push(routeMatches.ReportsPlansViewPath); // matches /reports/plans/{planId}

    if (currPath[1] === 'team-members')
      matches.push(routeMatches.ReportsTeamMembersPath); // matches /reports/team-members
    if (currPath[1] === 'team-members' && currPath.length === 3)
      matches.push(routeMatches.ReportsTeamMembersViewPath); // matches /reports/team-members/{teamMemberId}
    if (currPath[3] === 'team-member' && currPath.length === 4)
      matches.push(routeMatches.ReportsTeamMemberProgressViewPath); //matches /reports/plans/:planId/team-members/:teamMemberId

    if (currPath[1] === 'compliance')
      matches.push(routeMatches.ReportsCompliancePlansPath);
  }
};

export const getClickableLink = (link: string) =>
  link.startsWith('http://') || link.startsWith('https://')
    ? link
    : `http://${link}`;
